import React from 'react';
import classnames from 'classnames';

import { useMedia } from 'util/event';
import { getDateOnly } from 'util/helper';
import { BlogResource, BlogIndexPageResource } from 'util/api/swaggerApi/data-contracts';
import { getNewsCategoryTextById } from 'enums/newsType';

import { useBanner } from 'models/banner';
import { useNews } from 'models/news';
import { useTopics } from 'models/topics';

import Button from 'components/atoms/Button';
import Link from 'components/atoms/Link';
import Icon from 'components/atoms/Icon';
import SectionTitle from 'components/atoms/SectionTitle';
import ContentWrapper from 'components/atoms/ContentWrapper';
import CarouselBanner from 'components/molecules/CarouselBanner';
import ButtonMore from 'components/molecules/ButtonMore';
import CardNews from 'components/molecules/CardNews';
import CarouselTopic from 'components/molecules/CarouselTopic';
import CardTopics from 'components/molecules/CardTopics';

import IconFlag from 'images/icon/flag.inline.svg';
import IconMedia from 'images/icon/media.inline.svg';
import IconArrow from 'images/icon/arrow-right.inline.svg';
import Logo from 'images/ci/logo.inline.svg';
import backgroundDesktop from 'images/image/background-desktop.jpg';
import backgroundTablet from 'images/image/background-tablet.jpg';
import backgroundMobile from 'images/image/background-mobile.jpg';
import backgroundPureDesktop from 'images/image/background-pure-desktop.jpg';
import backgroundPureTablet from 'images/image/background-pure-tablet.jpg';
import backgroundPureMobile from 'images/image/background-pure-mobile.jpg';
import ImageAbout from 'images/image/homepage-about.png';
import IconPartnerDeco from 'images/icon/union.inline.svg';
import ImagePartnerCTRRA from 'images/image/partner-ctrra.inline.svg';
import ImageMobilePartnerCTRRA from 'images/image/partner-ctrra-mobile.inline.svg';
import ImagePartnerDotHan from 'images/image/partner-dot-han.inline.svg';
import ImageMobilePartnerDotHan from 'images/image/partner-dot-han-mobile.inline.svg';
import ImagePartnerCTCL from 'images/image/partner-ctcl.inline.svg';
import ImageMobilePartnerCTCL from 'images/image/partner-ctcl-mobile.inline.svg';

import styles from './index.module.css';

const newsIconMap = {
	賽事活動: IconFlag,
	焦點動態: IconMedia,
};

/**
 * RenderCardTopics: 根據 文章數量 & media 顯示不同樣式
 * media === 'desktop'
 * 1. 1-3 cards: 顯示 CardTopics
 * 2. 4+ cards: 顯示輪播 CarouselTopic
 *
 * media === 'phone'
 * 1. 1 card: 顯示 CardTopics
 * 2. 2+ cards: 顯示輪播 CarouselTopic
 */
const RenderCardTopics = ({ cards, media }: { cards: BlogIndexPageResource; media: string }) => {
	const { blogs, id } = cards;

	if (blogs && blogs.length > 3) {
		return (
			<CarouselTopic
				className={styles.carousel}
				data={blogs as BlogResource[]}
				uniqueId={`category-${id}`}
			/>
		);
	}

	if (media === 'desktop') {
		return (
			<div className={styles.cardTopicsWrapper}>
				{blogs &&
					blogs.map(item => (
						<CardTopics
							key={item.id}
							className={styles.cardTopics}
							type="basic"
							linkTo={`/topics/${item.id}`}
							image={item.image || ''}
							title={item.title || ''}
							subtitle={item.subtitle || ''}
						/>
					))}
			</div>
		);
	}

	// 以下是 media === 'phone'
	if (blogs && blogs.length > 1) {
		return (
			<CarouselTopic
				className={styles.carousel}
				data={blogs as BlogResource[]}
				uniqueId={`category-${id}`}
			/>
		);
	}

	return (
		<div className={classnames(styles.cardTopicsWrapper, styles.singleCard)}>
			{blogs &&
				blogs.map(item => (
					<CardTopics
						key={item.id}
						className={styles.cardTopics}
						type="basic"
						linkTo={`/topics/${item.id}`}
						image={item.image || ''}
						title={item.title || ''}
						subtitle={item.subtitle || ''}
					/>
				))}
		</div>
	);
};

const Home: React.FC = () => {
	const media = useMedia();
	const [{ banners }] = useBanner();
	const [{ newsList }] = useNews();
	const [{ topicsListForHomePage }] = useTopics();

	let newsSectionBackground;
	let aboutSectionBackground;

	switch (media) {
		case 'desktop':
			newsSectionBackground = backgroundDesktop;
			aboutSectionBackground = backgroundPureDesktop;
			break;
		case 'mobileSm':
			newsSectionBackground = backgroundMobile;
			aboutSectionBackground = backgroundPureMobile;
			break;
		default:
			newsSectionBackground = backgroundTablet;
			aboutSectionBackground = backgroundPureTablet;
	}

	const partnerLogo = [
		{
			id: 1,
			image: media === 'desktop' ? <ImagePartnerCTRRA /> : <ImageMobilePartnerCTRRA />,
		},
		{
			id: 2,
			image: media === 'desktop' ? <ImagePartnerDotHan /> : <ImageMobilePartnerDotHan />,
		},
		{
			id: 3,
			image: media === 'desktop' ? <ImagePartnerCTCL /> : <ImageMobilePartnerCTCL />,
		},
	];

	return (
		<>
			<div
				className={styles.backgroundFixed}
				style={{ backgroundImage: `url(${newsSectionBackground})` }}
			>
				{/* Banner */}
				{banners.length > 0 && <CarouselBanner data={banners} />}

				{/* 最新消息 */}
				<ContentWrapper className={styles.sectionNews}>
					<Link to="/news">
						<SectionTitle title="最新消息" subTitle="news" />
					</Link>
					{newsList &&
						Object.keys(newsList).map(category => {
							const newsListDataByCategory = newsList[Number(category)];
							const categoryText = getNewsCategoryTextById(Number(category));
							const newsIcon = newsIconMap[categoryText as keyof typeof newsIconMap];

							return (
								<div key={category} className={styles.block}>
									<SectionTitle type="categoryWithIcon" iconSrc={newsIcon} title={categoryText} />
									<div
										className={classnames(styles.cardWrapper, {
											[styles.singleCard]: newsListDataByCategory.length === 1,
										})}
									>
										{newsListDataByCategory && newsListDataByCategory.length > 0 && (
											<CardNews
												className={styles.featuredCard}
												linkTo={`/news/${newsListDataByCategory[0].id}`}
												layout="vertical"
												image={newsListDataByCategory[0].image || ''}
												date={getDateOnly(newsListDataByCategory[0].beginAt as string)}
												title={newsListDataByCategory[0].title || ''}
												subtitle={newsListDataByCategory[0].subtitle || ''}
											/>
										)}
										{newsListDataByCategory && newsListDataByCategory.length > 1 && (
											<div className={styles.cardList}>
												{newsListDataByCategory.slice(1).map(news => (
													<CardNews
														key={news.id}
														className={styles.cardItem}
														linkTo={`/news/${news.id}`}
														width="narrow"
														image={news.image || ''}
														date={getDateOnly(news?.beginAt as string) || ''}
														title={news.title || ''}
														subtitle={news.subtitle || ''}
													/>
												))}
											</div>
										)}
									</div>
									<ButtonMore
										className={styles.buttonMore}
										link="/news"
										query={`category=${category}`}
									/>
								</div>
							);
						})}
				</ContentWrapper>
			</div>

			{/* 編輯精選 */}
			<div className={styles.sectionTopicsWrapper}>
				<ContentWrapper className={styles.sectionTopics}>
					<Link className={styles.sectionTitle} to="/topics">
						<SectionTitle title="編輯精選" subTitle="topics" pattern="reverse" />
					</Link>

					{topicsListForHomePage.map(category => {
						if (category.blogs?.length === 0) return null;

						return (
							<div key={category.id} className={styles.block}>
								<Link
									className={styles.sectionTitle}
									to="/topics"
									query={`category=${category.id}`}
								>
									{category.name && (
										<SectionTitle type="category" pattern="reverse" title={category.name} />
									)}
								</Link>
								{RenderCardTopics({ cards: category, media })}
								<ButtonMore
									className={styles.buttonMore}
									type="reverse"
									link="/topics"
									query={`category=${category.id}`}
								/>
							</div>
						);
					})}
				</ContentWrapper>
			</div>

			{/* 關於去動 */}
			<div
				className={styles.backgroundFixed}
				style={{ backgroundImage: `url(${aboutSectionBackground})` }}
			>
				<ContentWrapper className={styles.sectionAbout}>
					<SectionTitle className={styles.sectionTitle} title="關於去動" subTitle="about" />
					<div className={styles.body}>
						<div className={styles.contentWrapper}>
							<Icon className={styles.logo} src={Logo} />
							<p className={styles.content}>
								去動，帶著動令，滿載活力，促進人們積極參與運動，同時去鼓勵探索和冒險的精神。去動不僅是一個名稱，更是一個激勵人心的使命，讓我們一起去發現新事物，保持活力，並共同創造更美好的未來。
								<br />
								<br />
								去動目標，擴大資源整合為宗旨，積極連結世界各大城市的運動休閒賽事與活動，讓國人有機會利用此健康大平台，走出台灣，以運動休閒交流，擴大生命視野，找出自己熱情，從中參與，積累出一頁頁活力、健康、精彩的篇章。
							</p>
							<Link to="/signup">
								<Button Icon={IconArrow}>註冊會員</Button>
							</Link>
						</div>
						<div className={styles.image}>
							<img src={ImageAbout} alt="" />
						</div>
					</div>
					{/* 因為客戶與合作夥伴的合作關係未確認，暫時移除該區塊 */}
					{/* <div className={styles.bottom}>
						<div className={styles.title}>
							<span>合作夥伴</span>
							<Icon className={styles.deco} src={IconPartnerDeco} />
						</div>
						<div className={styles.imageList}>
							{partnerLogo.map(logo => (
								<React.Fragment key={logo.id}>{logo.image}</React.Fragment>
							))}
						</div>
					</div> */}
				</ContentWrapper>
			</div>
		</>
	);
};

export default Home;
